import React, { useMemo, useState } from "react";
import DOMPurify from "dompurify";
import {
  FaChevronDown,
  FaChevronUp,
  FaRunning,
} from "react-icons/fa";

import { toAbsolutePhotoUrl } from "../../../services/disciplinePhotoServices";

const FALLBACK = "/img/no-image.jpg";

const stripHtml = (html = "") => {
  if (typeof window === "undefined") {
    return String(html || "").replace(/<[^>]*>/g, "");
  }

  const div = document.createElement("div");
  div.innerHTML = html;

  return div.textContent || div.innerText || "";
};

const getFallbackGradient = (index = 0) => {
  const gradients = [
    "from-orange-500 via-amber-500 to-yellow-400",
    "from-blue-600 via-sky-500 to-cyan-400",
    "from-purple-600 via-fuchsia-500 to-pink-400",
    "from-emerald-600 via-teal-500 to-cyan-400",
    "from-rose-600 via-red-500 to-orange-400",
    "from-indigo-600 via-blue-500 to-sky-400",
  ];

  return gradients[index % gradients.length];
};

export default function DisciplineCard({
  title = "Discipline",
  ageRange = "",
  imageSrc = "",
  textDiscipline = "",
  index = 0,
}) {
  const [expanded, setExpanded] = useState(false);

  const imageUrl = toAbsolutePhotoUrl(imageSrc) || "";

  const safeHtml = useMemo(() => {
    return DOMPurify.sanitize(textDiscipline || "", {
      USE_PROFILES: {
        html: true,
      },
      FORBID_ATTR: ["style"],
    });
  }, [textDiscipline]);

  const plainText = useMemo(() => {
    return stripHtml(textDiscipline || ageRange || "");
  }, [textDiscipline, ageRange]);

  const shortText = useMemo(() => {
    if (!plainText) {
      return "Découvrez cette activité proposée par le club, son ambiance, son fonctionnement et les informations utiles pour débuter.";
    }

    if (plainText.length <= 125) {
      return plainText;
    }

    return `${plainText.slice(0, 125).trim()}...`;
  }, [plainText]);

  const fallbackGradient = getFallbackGradient(index);
  const hasLongText = plainText.length > 125;

  const detailsId = `discipline-details-${String(title)
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/gi, "-")
    .replace(/^-+|-+$/g, "")}-${index}`;

  return (
    <article
      className="
        group relative mx-auto flex h-full w-full max-w-[340px] flex-col
        overflow-hidden rounded-[2rem]
        border border-slate-200 bg-white shadow-sm
        transition duration-300
        hover:-translate-y-2 hover:border-orange-300 hover:shadow-2xl
        dark:border-white/10 dark:bg-slate-900
        dark:hover:border-orange-500/40
      "
    >
      {/* Image */}
      <div className="relative h-60 shrink-0 overflow-hidden">
        {imageUrl ? (
          <img
            src={imageUrl}
            alt={title}
            className="
              h-full w-full object-cover
              transition duration-700
              group-hover:scale-110
            "
            loading="lazy"
            decoding="async"
            onError={(event) => {
              event.currentTarget.onerror = null;
              event.currentTarget.src = FALLBACK;
            }}
          />
        ) : (
          <div
            className={`
              flex h-full w-full items-center justify-center
              bg-gradient-to-br ${fallbackGradient}
            `}
          >
            <FaRunning className="text-6xl text-white/90 drop-shadow-xl" />
          </div>
        )}

        {/* Assombrissement pour rendre le titre lisible */}
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950/90 via-slate-950/25 to-transparent" />

        {/* Effet orange au survol */}
        <div
          className="
            absolute inset-0
            bg-gradient-to-br
            from-transparent via-transparent to-orange-500/30
            opacity-0 transition duration-500
            group-hover:opacity-100
          "
        />

        {/* Titre sur l'image */}
        <div className="absolute bottom-4 left-4 right-4">
          <h3 className="font-barlow text-2xl font-bold leading-tight text-white drop-shadow-lg">
            {title}
          </h3>

          {ageRange && (
            <p className="mt-2 line-clamp-2 text-sm font-medium leading-6 text-white/85">
              {stripHtml(ageRange)}
            </p>
          )}
        </div>
      </div>

      {/* Contenu */}
      <div className="flex flex-1 flex-col p-5">
        <div className="mb-4 flex items-center gap-2">
          <span className="h-2.5 w-2.5 rounded-full bg-orange-500 shadow-sm shadow-orange-500/40" />

          <span className="text-xs font-bold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-300">
            Description
          </span>
        </div>

        {!expanded && (
          <p className="text-sm leading-7 text-slate-600 dark:text-slate-300">
            {shortText}
          </p>
        )}

        {expanded && (
          <div
            id={detailsId}
            className="
              prose prose-slate max-w-none text-sm leading-7
              text-slate-600
              dark:prose-invert dark:text-slate-300

              prose-p:my-3
              prose-li:my-1

              prose-strong:text-slate-950
              dark:prose-strong:!text-white

              prose-b:text-slate-950
              dark:prose-b:!text-white

              prose-a:font-bold
              prose-a:text-orange-600
              hover:prose-a:text-orange-700

              dark:prose-a:text-orange-400
              dark:hover:prose-a:text-orange-300
            "
            dangerouslySetInnerHTML={{
              __html: safeHtml || `<p>${shortText}</p>`,
            }}
          />
        )}

        {hasLongText && (
          <button
            type="button"
            onClick={() => setExpanded((previous) => !previous)}
            aria-expanded={expanded}
            aria-controls={detailsId}
            className="
              mt-5 inline-flex items-center gap-2 self-start
              rounded-full border border-orange-200
              bg-orange-50 px-4 py-2
              text-sm font-bold text-orange-700
              transition duration-200
              hover:gap-3 hover:border-orange-300 hover:bg-orange-100

              dark:border-orange-500/30
              dark:bg-orange-500/10
              dark:text-orange-300
              dark:hover:border-orange-500/50
              dark:hover:bg-orange-500/20
              dark:hover:text-orange-200
            "
          >
            {expanded ? (
              <>
                Réduire
                <FaChevronUp className="text-xs" />
              </>
            ) : (
              <>
                Lire plus
                <FaChevronDown className="text-xs" />
              </>
            )}
          </button>
        )}
      </div>
    </article>
  );
}